import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { QrCode, Smartphone, TrendingUp, PlayCircle } from "lucide-react";
import { useInView } from "react-intersection-observer";

const Hero = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const { ref: videoRefInView, inView: videoInView } = useInView({
    triggerOnce: false,
    threshold: 0.4,
  });

  // Pause video when not in view
  useEffect(() => {
    if (videoRef.current && !videoInView) {
      videoRef.current.pause();
    }
  }, [videoInView]);

  // Toggle overlay play icon visibility
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    videoEl.addEventListener("play", handlePlay);
    videoEl.addEventListener("pause", handlePause);

    return () => {
      videoEl.removeEventListener("play", handlePlay);
      videoEl.removeEventListener("pause", handlePause);
    };
  }, []);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  return (
    <section className="max-w-7xl mx-auto w-full flex flex-col items-center bg-white py-20 px-4 sm:px-6 lg:px-8 relative">
      
                {/* 🎥 Video Section */}
        <motion.div
          ref={videoRefInView}
          animate={{
            scale: videoInView ? 1 : 0.95,
            opacity: videoInView ? 1 : 0.6,
          }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
          className="relative w-full max-w-4xl mt-12"
        >
          <video
            ref={videoRef}
            poster="https://cdn.prod.website-files.com/67f50baea549023b9a732b20/67f51194a286c35d8311fd2c_image%2078%20(1).png"
            src="https://zolaxbucket.s3.eu-north-1.amazonaws.com/zola-files/Zola-res-Vol-04.mp4"
            controls
            className="rounded-xl shadow-2xl w-full object-cover"
          />

          {!isPlaying && (
            <div
              onClick={handlePlay}
              className="absolute inset-0 flex items-center justify-center cursor-pointer"
            >
              <div className="bg-white/80 hover:bg-white/90 backdrop-blur-md rounded-full p-4 transition">
                <PlayCircle className="w-12 h-12 text-[#9333ea]" />
              </div>
            </div>
          )}
        </motion.div>
      <div className="flex flex-col md:flex-row w-full mt-20 items-center justify-between">
        {/* Content */}
        <div className="text-start md:w-1/2">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight"
          >
            Grow Your <span className="text-[#9333ea]">Restaurant</span>
            <br />
            with Smart Marketing
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-600 mt-4"
          >
            Transform one-time diners into loyal customers with our magic QR codes,
            automated review generation, and targeted marketing campaigns.
            Increase revenue while keeping costs low.
          </motion.p>
          
          {/* Stats */}
          <div className="flex flex-wrap justify-start gap-8 mt-12">
            {[
              { value: "95%", label: "Customer Retention" },
              { value: "3x", label: "More Reviews" },
              { value: "40%", label: "Revenue Increase" },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-bold text-[#9333ea]">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Feature Cards */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="relative"
        >
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="grid grid-cols-2 gap-6 max-w-md">
              <div className="bg-primary-100 rounded-xl p-6 text-center">
                <QrCode className="h-10 w-10 text-primary-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900">Magic QR</h3>
                <p className="text-sm text-gray-600 mt-2">Instant customer engagement</p>
              </div>
              <div className="bg-blue-100 rounded-xl p-6 text-center">
                <Smartphone className="h-10 w-10 text-blue-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900">Smart SMS</h3>
                <p className="text-sm text-gray-600 mt-2">Targeted promotions</p>
              </div>
              <div className="bg-green-100 rounded-xl p-6 text-center col-span-2">
                <TrendingUp className="h-10 w-10 text-green-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900">Growth Analytics</h3>
                <p className="text-sm text-gray-600 mt-2">Track your success in real-time</p>
              </div>
            </div>
          </div>

          {/* Floating Badges */}
          <div className="absolute -top-4 -right-4 bg-primary-500 text-white rounded-full px-4 py-2 text-xs font-semibold shadow-md animate-bounce">
            +250% ROI
          </div>
          <div className="absolute -bottom-4 -left-4 bg-green-500 text-white rounded-full px-4 py-2 text-xs font-semibold shadow-md animate-bounce delay-150">
            5⭐ Reviews
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
